import { useEffect, useRef, useState, type PointerEvent } from "react";
import type { TrendPoint } from "../../shared/telemetry";
import { fmtInt } from "./ui";

const height = 168;
const utcShort = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  timeZone: "UTC"
});
const utcLong = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
  timeZone: "UTC"
});
/** A UTC calendar date (or a timestamp's UTC date) as that date in every time zone. */
export function fmtUtcDay(value: string, year = false) {
  const at = new Date(`${value.slice(0, 10)}T00:00:00Z`);
  return (year ? utcLong : utcShort).format(at);
}

// A step of 1, 2 or 5 times a power of ten that puts at most four gridlines above zero.
function niceStep(max: number) {
  const raw = Math.max(max, 1) / 4;
  const power = 10 ** Math.floor(Math.log10(raw));
  return ([1, 2, 5, 10].map((m) => m * power).find((step) => step >= raw) ?? raw) || 1;
}

/** Splits the points into runs of consecutive reported days (indexes into `points`). */
function segments(points: TrendPoint[]) {
  const out: number[][] = [];
  let current: number[] = [];
  points.forEach((point, index) => {
    if (point.runs === null) {
      if (current.length) out.push(current);
      current = [];
    } else current.push(index);
  });
  if (current.length) out.push(current);
  return out;
}

export function trendSummary(points: TrendPoint[], floor: number) {
  const reported = points.filter((point) => point.runs !== null) as {
    date: string;
    runs: number;
  }[];
  const first = points[0]?.date;
  const last = points.at(-1)?.date;
  if (!reported.length)
    return `Recorded runs per UTC day, ${first} to ${last}: every day is withheld (fewer than ${floor} runs).`;
  const peak = reported.reduce((best, point) => (point.runs > best.runs ? point : best));
  const withheld = points.length - reported.length;
  return (
    `Recorded runs per UTC day, ${first} to ${last}: ${reported.length} of ${points.length} days reported, ` +
    `peak ${fmtInt(peak.runs)} on ${peak.date}, latest reported ${fmtInt(reported.at(-1)!.runs)} on ${reported.at(-1)!.date}` +
    (withheld ? `; ${withheld} withheld with fewer than ${floor} runs.` : ".")
  );
}

export function DailyTrend({ points, floor }: { points: TrendPoint[]; floor: number }) {
  const frame = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(640);
  const [hover, setHover] = useState<number>();

  useEffect(() => {
    const node = frame.current;
    if (!node) return;
    const measure = () => setWidth(Math.max(240, Math.round(node.clientWidth)));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  if (!points.length) return null;
  const values = points.flatMap((point) => (point.runs === null ? [] : [point.runs]));
  const step = niceStep(Math.max(0, ...values));
  const top = Math.max(step, Math.ceil(Math.max(0, ...values) / step) * step);
  const ticks = Array.from({ length: Math.round(top / step) + 1 }, (_, i) => i * step);
  // The left margin fits the widest tick label (about 7px a character at 11px tabular figures).
  const pad = { top: 12, right: 12, bottom: 26, left: Math.max(36, 12 + 7 * fmtInt(top).length) };
  const plotW = width - pad.left - pad.right;
  const plotH = height - pad.top - pad.bottom;
  const slot = points.length > 1 ? plotW / (points.length - 1) : 0;
  const x = (index: number) => pad.left + (points.length > 1 ? index * slot : plotW / 2);
  const y = (runs: number) => pad.top + plotH - (runs / top) * plotH;
  const baseline = y(0);
  const runs = segments(points);
  const withheld = points.length - values.length;
  const labelEvery = Math.max(1, Math.ceil(points.length / Math.max(2, Math.floor(plotW / 72))));
  const hovered = hover === undefined ? undefined : points[hover];

  const onPointerMove = (event: PointerEvent<SVGRectElement>) => {
    const box = event.currentTarget.getBoundingClientRect();
    const at = event.clientX - box.left;
    // The hit area starts half a slot before the first day, so each day owns one slot.
    const index = points.length > 1 ? Math.floor(at / slot) : 0;
    setHover(Math.min(points.length - 1, Math.max(0, index)));
  };

  return (
    <figure className="trend">
      <figcaption>
        <h3>Recorded runs per day (UTC)</h3>
      </figcaption>
      <div className="trend-frame" ref={frame}>
        <svg
          width={width}
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          role="img"
          aria-label={trendSummary(points, floor)}
        >
          {points.map((point, index) =>
            point.runs === null ? (
              <rect
                key={point.date}
                className="trend-withheld"
                // A lone day owns the whole plot; otherwise each day owns one slot.
                x={points.length > 1 ? Math.max(pad.left, x(index) - slot / 2) : pad.left}
                width={
                  points.length > 1
                    ? Math.max(
                        2,
                        Math.min(width - pad.right, x(index) + slot / 2) -
                          Math.max(pad.left, x(index) - slot / 2)
                      )
                    : plotW
                }
                y={pad.top}
                height={plotH}
              />
            ) : null
          )}
          {ticks.map((tick) => (
            <g key={tick}>
              <line
                className="trend-grid"
                x1={pad.left}
                x2={width - pad.right}
                y1={y(tick)}
                y2={y(tick)}
              />
              <text
                className="trend-tick"
                x={pad.left - 8}
                y={y(tick)}
                dy="0.32em"
                textAnchor="end"
              >
                {fmtInt(tick)}
              </text>
            </g>
          ))}
          {points.map((point, index) => {
            const last = index === points.length - 1;
            const shown =
              last || (index % labelEvery === 0 && points.length - 1 - index >= labelEvery * 0.6);
            return shown ? (
              <text
                key={point.date}
                className="trend-tick"
                x={x(index)}
                y={height - 8}
                textAnchor={index === 0 ? "start" : last ? "end" : "middle"}
              >
                {fmtUtcDay(point.date)}
              </text>
            ) : null;
          })}
          {runs.map((run) =>
            run.length > 1 ? (
              <g key={run[0]}>
                <path
                  className="trend-area"
                  d={`M${x(run[0]!)},${baseline}${run.map((i) => `L${x(i)},${y(points[i]!.runs!)}`).join("")}L${x(run.at(-1)!)},${baseline}Z`}
                />
                <polyline
                  className="trend-line"
                  points={run.map((i) => `${x(i)},${y(points[i]!.runs!)}`).join(" ")}
                />
              </g>
            ) : (
              <circle
                key={run[0]}
                className="trend-dot"
                cx={x(run[0]!)}
                cy={y(points[run[0]!]!.runs!)}
                r={4}
              />
            )
          )}
          {hovered && hover !== undefined && (
            <g className="trend-hover" aria-hidden="true">
              <line x1={x(hover)} x2={x(hover)} y1={pad.top} y2={baseline} />
              {hovered.runs !== null && (
                <circle className="trend-dot" cx={x(hover)} cy={y(hovered.runs)} r={4} />
              )}
            </g>
          )}
          <rect
            className="trend-hit"
            x={pad.left - slot / 2}
            y={0}
            width={plotW + slot}
            height={height}
            onPointerMove={onPointerMove}
            onPointerLeave={() => setHover(undefined)}
          />
        </svg>
        {hovered && hover !== undefined && (
          <div
            className="trend-tip"
            aria-hidden="true"
            style={{
              left: Math.min(Math.max(x(hover), 70), width - 70),
              top: pad.top
            }}
          >
            <span>{fmtUtcDay(hovered.date)}</span>
            <strong className="num">
              {hovered.runs === null ? `Withheld (under ${floor})` : `${fmtInt(hovered.runs)} runs`}
            </strong>
          </div>
        )}
      </div>
      <p className="foot-note trend-note">
        {withheld > 0 && <span className="trend-key" aria-hidden="true" />}
        <span>
          {withheld > 0 &&
            `Shaded days had fewer than ${floor} recorded runs, or none, and are withheld rather than drawn as zero. `}
          The range is a rolling window, so its first and last days are partial.
        </span>
      </p>
    </figure>
  );
}
